import React from 'react';
import styled from 'styled-components';

import ProsperCard from '../../assets/card-isometric.svg';
import KiteInSky from '../../assets/icons/kite-in-sky.svg';
import ReentryCheck from '../../assets/icons/reentry.svg';
import {
  CARD_BREAKPOINT,
  colors,
  MOBILE_BREAKPOINT,
  SUPPORT_PHONE_NUMBER_HREF,
} from '../constants';

import { PrimaryButton } from './Button';
import Heading from './Heading';
import { Spinner } from './Spinner';
import SubHeading from './SubHeading';

// Card -----------------------------------------------------------------------------------------
// Container centers children in a column starting from the top
const Card = styled.div`
  position: relative;
  width: 40rem;
  min-height: 34rem;
  max-width: 100%;
  margin: 0 2rem;
  border-radius: 0.25rem;
  padding: 0 2.5rem;
  box-shadow: 0 0.0625rem 0.25rem rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background-color: #ffffff;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1rem;
  }
`;
Card.displayName = 'Card';

// CenteredCard -----------------------------------------------------------------------------------
export const CenteredCard = styled(Card)`
  justify-content: center;
`;
CenteredCard.displayName = 'CenteredCard';

// LoadingCard ------------------------------------------------------------------------------------
export const LoadingCard = () => (
  <CenteredCard>
    <Spinner />
  </CenteredCard>
);

// ErrorCard --------------------------------------------------------------------------------------
const ErrorIconContainer = styled.div`
  margin: 6.4375rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 5.4375rem 0 0;
  }
`;
ErrorIconContainer.displayName = 'ErrorIconContainer';

const PhoneLink = styled.a`
  white-space: pre;
  text-decoration: none;
  appearance: none;
  color: ${colors.TEXT_ACTION};
`;
PhoneLink.displayName = 'PhoneLink';

const ErrorCardHeading = styled(Heading)`
  margin: 1.375rem 0 0.875rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    max-width: 18rem;
    margin: 1.1875rem 0 1rem;
  }
`;
ErrorCardHeading.displayName = 'ErrorCardHeading';

const ErrorCardSubHeading = styled(SubHeading)`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0.875rem 0.9375rem;
  }
`;
ErrorCardSubHeading.displayName = 'ErrorCardSubHeading';

const ErrorCardIcon = () => (
  <ErrorIconContainer>
    <KiteInSky />
  </ErrorIconContainer>
);

const ErrorCardPhoneLink = () => (
  <PhoneLink
    id="error-card-phone-number-link"
    href={SUPPORT_PHONE_NUMBER_HREF}
    aria-label="Call us at 800-851-0630"
  >
    &nbsp;(800)&nbsp;851-0630&nbsp;
  </PhoneLink>
);

// ErrorCard - TODO: show on page load errors and behind ErrorModal
export const ErrorCard = () => (
  <Card>
    <ErrorCardIcon />
    <ErrorCardHeading id="error-card-heading">
      Sorry, something went wrong on our end
    </ErrorCardHeading>
    <ErrorCardSubHeading id="error-card-subheading">
      Please try again. If you’re still having issues, reach out to us at
      <ErrorCardPhoneLink />
      9am-7pm EST Mon-Fri for assistance
    </ErrorCardSubHeading>
  </Card>
);

// ExportErrorCard --------------------------------------------------------------------------------
const ExportCard = styled(Card)`
  min-height: 24rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 29.5rem;
  }
`;
ExportCard.displayName = 'ExportCard';

const ExportErrorIconContainer = styled.div`
  margin: 3.9375rem 0 0;
`;
ExportErrorIconContainer.displayName = 'ExportErrorIconContainer';

const ExportErrorCardHeading = styled(Heading)`
  margin: 1.375rem 0 0.875rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 18rem;
    margin: 1rem 0 1.25rem;
  }
`;
ErrorCardHeading.displayName = 'ErrorCardHeading';

const ExportErrorCardSubHeading = styled(SubHeading)`
  max-width: 29.3125rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    padding: 0 0.25rem;
  }
`;
ErrorCardSubHeading.displayName = 'ErrorCardSubHeading';

const ExportPhoneLink = styled.a`
  text-decoration: none;
  appearance: none;
  color: ${colors.TEXT_ACTION};
  &:hover {
    text-decoration: underline;
  }
`;
ExportPhoneLink.displayName = 'ExportPhoneLink';

const ExportErrorCardPhoneLink = () => (
  <ExportPhoneLink
    id="export-error-card-phone-number-link"
    href={SUPPORT_PHONE_NUMBER_HREF}
  >
    {' '}
    800-851-0630
  </ExportPhoneLink>
);

// Error card shown for EXPORT_ERROR application status
export const ExportErrorCard = () => (
  <ExportCard>
    <ExportErrorIconContainer>
      <KiteInSky />
    </ExportErrorIconContainer>
    <ExportErrorCardHeading id="error-card-heading">
      Sorry, something went wrong on our end
    </ExportErrorCardHeading>
    <ExportErrorCardSubHeading id="export-error-card-subheading">
      We’re actively working on the problem, and we’ll email you once it’s
      resolved. It shouldn’t take more than a few hours, but if you haven’t
      heard from us in one business day, please reach out at
      <ExportErrorCardPhoneLink /> 9am-7pm ET Mon-Fri for assistance.
    </ExportErrorCardSubHeading>
  </ExportCard>
);

// RetryWorkflowEventCard -------------------------------------------------------------------------
interface RetryWorkflowEventProps {
  onRetry: Function;
}

const RetryButton = styled(PrimaryButton)`
  margin: 1.375rem 0 0;
  width: 15.3125rem;
`;
RetryButton.displayName = 'RetryButton';

// Looks like an ErrorCard with a button labeled 'Retry'
// Shown when an ERROR status is polled in a waiting room
// Uses callback from props when the button is clicked
export const RetryWorkflowEventCard = (props: RetryWorkflowEventProps) => {
  const { onRetry } = props;
  return (
    <Card>
      <ErrorCardIcon />
      <ErrorCardHeading id="retry-workflow-heading">
        Sorry, something went wrong on our end
      </ErrorCardHeading>
      <ErrorCardSubHeading id="retry-workflow-subheading">
        Please try again. If you’re still having issues, reach out to us at
        <ErrorCardPhoneLink />
        9am-7pm EST Mon-Fri for assistance
      </ErrorCardSubHeading>
      <RetryButton
        id="retry-workflow-button"
        type="button"
        aria-label="Retry"
        onClick={() => onRetry()}
      >
        Retry
      </RetryButton>
    </Card>
  );
};

// InputReentryCard -------------------------------------------------------------------------------
interface InputReentryProps {
  heading: string;
  subheading: string;
  fieldname: string;
  onReenterSubmit: Function;
  infoAboveForm?: string;
  children?: React.ReactNode;
}

const ReentryCard = styled(Card)`
  min-height: 28rem;
`;
ReentryCard.displayName = 'ReentryCard';

const ReentryIconContainer = styled.div`
  margin: 2.875rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 2.4375rem 0 0;
  }
`;
ReentryIconContainer.displayName = 'ReentryIconContainer';

const ReentryCardHeading = styled(Heading)`
  margin: 1.25rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 18rem;
    margin: 1.375rem 0 0;
  }
`;
ReentryCardHeading.displayName = 'ReentryCardHeading';

const ReentryCardSubHeading = styled(SubHeading)`
  max-width: 22rem;
  margin: 1.3125rem 0 0;
  line-height: 1.5;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 17rem;
    margin: 1.625rem 0 0;
  }
`;
ReentryCardSubHeading.displayName = 'ReentryCardSubHeading';

const ReentryFormContainer = styled.div`
  width: 100%;
  max-width: 25rem;
  margin: 1.1875rem 0 4.375rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 21.4375rem;
    margin: 1rem 0 4.375rem;
  }
`;
ReentryFormContainer.displayName = 'ReentryFormContainer';

const ReentrySubmitButton = styled(PrimaryButton)`
  width: 100%;
  margin: 3.9375rem 0 0;
`;
ReentrySubmitButton.displayName = 'ReentrySubmitButton';

const ReentryInfoAboveForm = styled(SubHeading)`
  margin: 1rem 0.125rem 0;
  font-size: 0.875rem;
  line-height: 1.375rem;
`;
ReentryInfoAboveForm.displayName = 'ReentryInfoAboveForm';

export const InputReentryCard = (props: InputReentryProps) => {
  const {
    heading,
    subheading,
    fieldname,
    onReenterSubmit,
    children,
    infoAboveForm,
  } = props;

  return (
    <ReentryCard>
      <ReentryIconContainer>
        <ReentryCheck />
      </ReentryIconContainer>
      <ReentryCardHeading id={`${fieldname}-reentry-card-heading`}>
        {heading}
      </ReentryCardHeading>
      <ReentryCardSubHeading id={`${fieldname}-reentry-card-subheading`}>
        {subheading}
      </ReentryCardSubHeading>
      <ReentryFormContainer>
        {children}
        <ReentryInfoAboveForm>{infoAboveForm}</ReentryInfoAboveForm>
        <ReentrySubmitButton
          id={`${fieldname}-reentry-submit-button`}
          type="button"
          onClick={onReenterSubmit}
        >
          Continue
        </ReentrySubmitButton>
      </ReentryFormContainer>
    </ReentryCard>
  );
};

InputReentryCard.defaultProps = {
  infoAboveForm: null,
  children: null,
};

// InputUpdateCard --------------------------------------------------------------------------------
interface InputUpdateProps {
  heading: string;
  fieldname: string;
  onUpdateSubmit: Function;
  children?: React.ReactNode;
  iconBelowForm?: React.ReactNode;
  infoBelowForm?: string;
  submitButtonText?: string;
}

const UpdateCard = styled(Card)`
  min-height: 28rem;
`;
UpdateCard.displayName = 'UpdateCard';

const UpdateIconContainer = styled.div`
  margin: 3.1875rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 2.4375rem 0 0;
  }
`;
UpdateIconContainer.displayName = 'UpdateIconContainer';

const UpdateCardHeading = styled(Heading)`
  margin: 0.25rem 0 0.875rem;
  line-height: 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    margin: 0.25rem 0 0.875rem;
  }
`;
UpdateCardHeading.displayName = 'UpdateCardHeading';

const UpdateCardSubHeading = styled(SubHeading)`
  max-width: 25rem;
  margin: 1.625rem 0 0;
  text-align: left;
  line-height: 1.375rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 21.4375rem;
    margin: 1.625rem auto 0;
    line-height: 1.375rem;
  }
`;
UpdateCardSubHeading.displayName = 'UpdateCardSubHeading';

const UpdateFormContainer = styled.div`
  width: 100%;
  max-width: 25rem;
  margin: 0.75rem 0 3.375rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 21.4375rem;
    margin: 0.75rem 0 2.1875rem;
  }
`;
UpdateFormContainer.displayName = 'UpdateFormContainer';

const UpdateSubmitButton = styled(PrimaryButton)`
  width: 100%;
  margin: 2.25rem auto 0;
`;
UpdateSubmitButton.displayName = 'UpdateSubmitButton';

const UpdateInfoBelowForm = styled(SubHeading)`
  margin: 0.8125rem auto 0;
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
UpdateInfoBelowForm.displayName = 'UpdateInfoBelowForm';

export const InputUpdateCard = (props: InputUpdateProps) => {
  const {
    heading,
    fieldname,
    onUpdateSubmit,
    children,
    iconBelowForm,
    infoBelowForm,
    submitButtonText,
  } = props;

  return (
    <UpdateCard>
      <UpdateIconContainer>
        <ProsperCard />
      </UpdateIconContainer>
      <UpdateCardHeading id={`${fieldname}-update-card-heading`}>
        {heading}
      </UpdateCardHeading>
      <UpdateFormContainer>
        {children}
        <UpdateSubmitButton
          id={`${fieldname}-update-submit-button`}
          type="button"
          onClick={onUpdateSubmit}
        >
          {submitButtonText}
        </UpdateSubmitButton>
        <UpdateInfoBelowForm>
          {iconBelowForm}
          {infoBelowForm}
        </UpdateInfoBelowForm>
      </UpdateFormContainer>
    </UpdateCard>
  );
};

InputUpdateCard.defaultProps = {
  children: null,
  iconBelowForm: null,
  infoBelowForm: null,
  submitButtonText: '',
};

export default Card;
